/**
 * MailboxWorkerKeyTab — Global Admin
 * Manage the global API key that authenticates the mailbox worker service to the backend.
 * Key is shown in plaintext exactly once at generation; only the prefix is stored for display.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, Copy01, Eye, EyeOff, Key01, Trash01 } from "@untitledui/icons";
import { DialogTrigger as AriaDialogTrigger, Heading as AriaHeading } from "react-aria-components";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { BackgroundPattern } from "@/components/shared-assets/background-patterns";
import { useAdminApi } from "@/services/api/admin-api";
import type { MailboxWorkerKeyMetadataDto } from "@/types/api/admin";

// =============================================================================
// CONFIRMATION MODAL
// =============================================================================

function ConfirmationModal({
    isOpen,
    onOpenChange,
    title,
    message,
    confirmLabel,
    confirmColor,
    icon,
    iconColor,
    isLoading,
    onConfirm,
}: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    message: string;
    confirmLabel: string;
    confirmColor: "primary" | "primary-destructive";
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    iconColor: "warning" | "error";
    isLoading: boolean;
    onConfirm: () => void;
}) {
    return (
        <AriaDialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
            <ModalOverlay isDismissable={!isLoading}>
                <Modal>
                    <Dialog>
                        <div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-xl sm:max-w-100">
                            <CloseButton onClick={() => onOpenChange(false)} theme="light" size="lg" className="absolute top-3 right-3" />

                            <div className="flex flex-col gap-4 px-4 pt-5 sm:px-6 sm:pt-6">
                                <div className="relative w-max">
                                    <FeaturedIcon color={iconColor} size="lg" theme="light" icon={icon} />
                                    <BackgroundPattern pattern="circle" size="sm" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                                </div>

                                <div className="z-10 flex flex-col gap-0.5">
                                    <AriaHeading slot="title" className="text-md font-semibold text-primary">
                                        {title}
                                    </AriaHeading>
                                    <p className="text-sm text-tertiary">{message}</p>
                                </div>
                            </div>

                            <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                                <Button color="secondary" size="lg" onClick={() => onOpenChange(false)} isDisabled={isLoading}>
                                    Cancel
                                </Button>
                                <Button color={confirmColor} size="lg" onClick={onConfirm} isLoading={isLoading}>
                                    {confirmLabel}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </AriaDialogTrigger>
    );
}

// =============================================================================
// PLAINTEXT KEY REVEAL
// =============================================================================

function PlaintextKeyReveal({ plaintextKey, onDismiss }: { plaintextKey: string; onDismiss: () => void }) {
    const [isRevealed, setIsRevealed] = useState(false);
    const [copied, setCopied] = useState(false);
    const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(plaintextKey);
            setCopied(true);
            if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
            copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API may fail in insecure contexts
        }
    };

    useEffect(() => {
        return () => {
            if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
        };
    }, []);

    return (
        <div className="rounded-lg border border-brand-solid bg-brand-primary_alt p-4">
            <div className="mb-3 flex items-center gap-2">
                <Key01 className="size-4 text-brand-secondary" />
                <span className="text-sm font-medium text-brand-secondary">New API Key Generated</span>
            </div>

            <div className="relative rounded-md border border-secondary bg-primary p-3">
                <code className="block pr-20 font-mono text-sm break-all text-primary">
                    {isRevealed ? plaintextKey : "\u2022".repeat(Math.min(plaintextKey.length, 48))}
                </code>

                <div className="absolute top-2 right-2 flex items-center gap-1">
                    <button
                        onClick={() => setIsRevealed((v) => !v)}
                        className="rounded-md p-1.5 text-quaternary transition-colors hover:bg-secondary hover:text-secondary"
                        title={isRevealed ? "Hide key" : "Reveal key"}
                    >
                        {isRevealed ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                    <button
                        onClick={handleCopy}
                        className="flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-secondary transition-colors hover:bg-secondary"
                        title="Copy to clipboard"
                    >
                        {copied ? (
                            <>
                                <Check className="size-3.5 text-fg-success-secondary" />
                                <span className="text-fg-success-secondary">Copied!</span>
                            </>
                        ) : (
                            <>
                                <Copy01 className="size-3.5" />
                                <span>Copy</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            <p className="mt-3 text-sm font-medium text-warning-primary">This key will not be shown again. Copy it now.</p>

            <div className="mt-4">
                <Button color="secondary" size="sm" onClick={onDismiss}>
                    Done
                </Button>
            </div>
        </div>
    );
}

// =============================================================================
// KEY METADATA DISPLAY
// =============================================================================

function KeyMetadataDisplay({ metadata, onRegenerate, onRevoke }: { metadata: MailboxWorkerKeyMetadataDto; onRegenerate: () => void; onRevoke: () => void }) {
    const formattedDate = metadata.generatedAt
        ? new Date(metadata.generatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
        : null;

    return (
        <div className="rounded-lg border border-secondary p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-lg border border-secondary bg-secondary">
                        <Key01 className="size-5 text-quaternary" />
                    </div>
                    <div>
                        <p className="font-mono text-sm text-primary">
                            {metadata.keyPrefix}
                            <span className="text-quaternary">{"\u2022".repeat(8)}</span>
                        </p>
                        {formattedDate && <p className="text-xs text-tertiary">Generated {formattedDate}</p>}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button color="secondary" size="sm" onClick={onRegenerate}>
                        Regenerate
                    </Button>
                    <Button color="secondary-destructive" size="sm" iconLeading={Trash01} onClick={onRevoke}>
                        Revoke
                    </Button>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// EMPTY STATE
// =============================================================================

function EmptyState({ onGenerate, isGenerating }: { onGenerate: () => void; isGenerating: boolean }) {
    return (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-secondary py-12">
            <div className="flex size-12 items-center justify-center rounded-lg border border-secondary bg-secondary">
                <Key01 className="size-6 text-quaternary" />
            </div>
            <p className="mt-4 text-sm font-medium text-primary">No API key</p>
            <p className="mt-1 text-sm text-tertiary">No API key has been generated for the mailbox worker.</p>
            <div className="mt-4">
                <Button color="primary" size="sm" onClick={onGenerate} isLoading={isGenerating}>
                    Generate Key
                </Button>
            </div>
        </div>
    );
}

// =============================================================================
// MAILBOX WORKER KEY TAB
// =============================================================================

export function MailboxWorkerKeyTab() {
    const adminApi = useAdminApi();

    const [metadata, setMetadata] = useState<MailboxWorkerKeyMetadataDto | null>(null);
    const [plaintextKey, setPlaintextKey] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isRevoking, setIsRevoking] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showRegenerateModal, setShowRegenerateModal] = useState(false);
    const [showRevokeModal, setShowRevokeModal] = useState(false);

    const fetchMetadata = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await adminApi.getMailboxWorkerKey();
            setMetadata(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load API key metadata.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi]);

    useEffect(() => {
        fetchMetadata();
    }, [fetchMetadata]);

    const handleGenerate = useCallback(async () => {
        setIsGenerating(true);
        setError(null);
        try {
            const result = await adminApi.generateMailboxWorkerKey();
            setPlaintextKey(result.apiKey);
            setMetadata({ hasKey: true, keyPrefix: result.keyPrefix, generatedAt: result.generatedAt });
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to generate API key.");
        } finally {
            setIsGenerating(false);
        }
    }, [adminApi]);

    const handleRegenerate = useCallback(async () => {
        setIsGenerating(true);
        setError(null);
        try {
            const result = await adminApi.generateMailboxWorkerKey();
            setPlaintextKey(result.apiKey);
            setMetadata({ hasKey: true, keyPrefix: result.keyPrefix, generatedAt: result.generatedAt });
            setShowRegenerateModal(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to regenerate API key.");
        } finally {
            setIsGenerating(false);
        }
    }, [adminApi]);

    const handleRevoke = useCallback(async () => {
        setIsRevoking(true);
        setError(null);
        try {
            await adminApi.revokeMailboxWorkerKey();
            setMetadata({ hasKey: false, keyPrefix: null, generatedAt: null });
            setPlaintextKey(null);
            setShowRevokeModal(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to revoke API key.");
        } finally {
            setIsRevoking(false);
        }
    }, [adminApi]);

    const handleDismissPlaintext = useCallback(() => {
        setPlaintextKey(null);
    }, []);

    // Loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <LoadingIndicator size="md" label="Loading API key..." />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div>
                <h3 className="text-md font-semibold text-primary">Mailbox Worker API Key</h3>
                <p className="mt-1 text-sm text-tertiary">
                    This key authenticates the mailbox worker service to the backend. Set it as{" "}
                    <code className="rounded bg-secondary px-1 py-0.5 font-mono text-xs">API_KEY</code> in the App Service configuration.
                </p>
            </div>

            {error && <p className="text-sm text-error-primary">{error}</p>}

            {/* Plaintext reveal takes priority when a key was just generated */}
            {plaintextKey ? (
                <PlaintextKeyReveal plaintextKey={plaintextKey} onDismiss={handleDismissPlaintext} />
            ) : metadata?.hasKey ? (
                <KeyMetadataDisplay metadata={metadata} onRegenerate={() => setShowRegenerateModal(true)} onRevoke={() => setShowRevokeModal(true)} />
            ) : (
                <EmptyState onGenerate={handleGenerate} isGenerating={isGenerating} />
            )}

            {/* Regenerate confirmation modal */}
            <ConfirmationModal
                isOpen={showRegenerateModal}
                onOpenChange={setShowRegenerateModal}
                title="Regenerate API Key"
                message="Regenerating this key will immediately invalidate the current key. The mailbox worker will fail to authenticate until you update API_KEY in the App Service configuration."
                confirmLabel="Regenerate"
                confirmColor="primary"
                icon={AlertTriangle}
                iconColor="warning"
                isLoading={isGenerating}
                onConfirm={handleRegenerate}
            />

            {/* Revoke confirmation modal */}
            <ConfirmationModal
                isOpen={showRevokeModal}
                onOpenChange={setShowRevokeModal}
                title="Revoke API Key"
                message="Revoking this key will immediately disconnect the mailbox worker from all clients."
                confirmLabel="Revoke"
                confirmColor="primary-destructive"
                icon={Trash01}
                iconColor="error"
                isLoading={isRevoking}
                onConfirm={handleRevoke}
            />
        </div>
    );
}
